import { useState } from "react";
import { Bell, Check } from "lucide-react";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";

/**
 * Prominent "Subscribe for Game Alerts" widget. Reuses the existing
 * POST /api/newsletter endpoint with `source` tagging so admins can later
 * segment game-alert subscribers from generic newsletter signups.
 */
export default function GameAlertSignup({ source = "game-alerts" }) {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await api.post("/newsletter", { email, source });
            setDone(true);
            setEmail("");
            toast.success("You're on the list. Crown alerts incoming.");
        } catch (err) {
            toast.error(formatErr(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            className="relative border-y border-line overflow-hidden"
            data-testid="game-alert-signup"
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-gradient-to-r from-bg via-purple-dark to-bg" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[640px] rounded-full bg-gold/5 blur-3xl" />

            <div className="relative max-w-5xl mx-auto px-5 md:px-10 py-20 md:py-24 text-center">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-gold/40 text-gold text-[10px] tracking-[0.3em] uppercase font-bold">
                    <Bell size={12} /> Game Alerts
                </div>
                <h2 className="h-display text-5xl md:text-6xl text-white mt-5 leading-[0.95]">
                    Never Miss <span className="text-gold">Tip-Off.</span>
                </h2>
                <p className="text-white/70 text-base md:text-lg max-w-xl mx-auto mt-5 leading-relaxed">
                    Drop your email — we&apos;ll send next-game reminders, final scores, and exclusive ticket pre-sales straight to your inbox.
                </p>

                <form
                    onSubmit={submit}
                    className="mt-9 flex flex-col sm:flex-row max-w-xl mx-auto gap-3"
                    data-testid="game-alert-form"
                >
                    <input
                        type="email"
                        required
                        value={email}
                        disabled={done || loading}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@kingdom.com"
                        data-testid="game-alert-email"
                        className="flex-1 px-5 py-4 text-sm bg-bg-soft border border-line focus:border-gold outline-none transition-colors disabled:opacity-50"
                    />
                    <button
                        type="submit"
                        disabled={loading || done}
                        data-testid="game-alert-submit"
                        className="btn-gold px-7 py-4 text-xs tracking-[0.2em] uppercase font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                        {done ? <><Check size={14} /> You&apos;re In</> : (loading ? "Joining…" : "Get Alerts")}
                    </button>
                </form>

                <p className="text-ink text-xs mt-5">
                    No spam. Unsubscribe anytime. We respect the royal court.
                </p>
            </div>
        </section>
    );
}
